import {
  PANDECTES_RUNTIME_EVENT_NAME,
  PANDECTES_ANALYTICS_EVENT_NAME,
  PANDECTES_WINDOW_QUEUE_KEY,
  PANDECTES_WINDOW_LATEST_DETAIL_KEY,
  normalizePandectesConsent,
} from './pandectes-consent.js';

/**
 * Custom Analytics Tracking - Shopify Custom Pixel Approach
 * Publishes events via Shopify.analytics.publish to be consumed by Custom Pixel
 */

// Initialize dataLayer on main window (required for Custom Pixel to push events)
window.dataLayer = window.dataLayer || [];

// Listen for analytics events from Custom Pixel (via postMessage)
window.addEventListener('message', function (event) {
  if (event.data && event.data.type === 'ARDENE_ANALYTICS_EVENT') {
    window.dataLayer.push(event.data.payload);
    console.log('[Analytics] Received event from Custom Pixel:', event.data.payload.event);
  }
});

/**
 * Utilities
 */
function publishAnalytics(eventName, payload) {
  if (!window.Shopify?.analytics?.publish) {
    console.error('Shopify.analytics.publish not available');
    return false;
  }
  window.Shopify.analytics.publish(eventName, payload);
  return true;
}

let lastPublishedPandectesConsentSignature = '';

function publishPandectesConsent(detail) {
  const payload = normalizePandectesConsent(detail);
  if (!payload) return false;

  const payloadSignature = JSON.stringify(payload);
  if (payloadSignature === lastPublishedPandectesConsentSignature) {
    return false;
  }

  const didPublish = publishAnalytics(PANDECTES_ANALYTICS_EVENT_NAME, payload);

  if (didPublish) {
    lastPublishedPandectesConsentSignature = payloadSignature;
  }

  return didPublish;
}

function flushPendingPandectesConsent() {
  const queuedDetails = Array.isArray(window[PANDECTES_WINDOW_QUEUE_KEY])
    ? [...window[PANDECTES_WINDOW_QUEUE_KEY]]
    : [];

  window[PANDECTES_WINDOW_QUEUE_KEY] = [];

  if (queuedDetails.length > 0) {
    queuedDetails.forEach((detail) => {
      publishPandectesConsent(detail);
    });
    return;
  }

  if (window[PANDECTES_WINDOW_LATEST_DETAIL_KEY]) {
    publishPandectesConsent(window[PANDECTES_WINDOW_LATEST_DETAIL_KEY]);
  }
}

function initPandectesConsentTracking() {
  window[PANDECTES_WINDOW_QUEUE_KEY] = Array.isArray(window[PANDECTES_WINDOW_QUEUE_KEY])
    ? window[PANDECTES_WINDOW_QUEUE_KEY]
    : [];

  window.addEventListener(PANDECTES_RUNTIME_EVENT_NAME, function (event) {
    publishPandectesConsent(event?.detail);
  });

  flushPendingPandectesConsent();
}

function normalizeEmailForHash(email) {
  return String(email || '').trim().toLowerCase();
}

async function sha256Hex(value) {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Derives the Shopify market language and country from the URL path prefix,
 * falling back to document.documentElement.lang (always set from request.locale.iso_code).
 * Returns { language, country, locale } — country may be null for the default market.
 */
function getLocaleData() {
  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);
  const firstSeg = segments[0] || '';
  if (/^[a-z]{2}(-[a-z]{2})?$/i.test(firstSeg)) {
    const parts = firstSeg.toLowerCase().split('-');
    return {
      language: parts[0],
      country: parts[1] ? parts[1].toUpperCase() : null,
      locale: firstSeg.toLowerCase(),
    };
  }
  const lang = (document.documentElement.lang || '').toLowerCase();
  return { language: lang || null, country: null, locale: lang || null };
}

/** SHA-256 hex of logged-in customer email for pixel payloads only (no plain email on the wire). */
async function getCustomerEmailSha256ForPixel() {
  const raw = window.ardeneCustomerEmail;
  if (!raw || typeof raw !== 'string') return null;
  const normalized = normalizeEmailForHash(raw);
  if (!normalized) return null;
  try {
    return await sha256Hex(normalized);
  } catch (e) {
    console.error('[Analytics] sha256 customer email failed:', e);
    return null;
  }
}

function getActiveCurrencyOrDefault() {
  return window.Shopify?.currency?.active || 'CAD';
}

function splitInternalCategories(internalCategoryCsv) {
  const parts = (internalCategoryCsv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts;
}

// Append item_category, item_category2... to base item from CSV
function applyCategoryFieldsToItem(baseItem, internalCategoryCsv) {
  const parts = splitInternalCategories(internalCategoryCsv);
  if (parts.length === 0) return baseItem;
  parts.forEach((val, idx) => {
    const key = idx === 0 ? 'item_category' : `item_category${idx + 1}`;
    baseItem[key] = val;
  });
  return baseItem;
}

function parseCentsToMajorUnits(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  // Value is in cents, convert to major currency
  return Math.round(n) / 100;
}

function calculateDiscount(price, compareAtPrice) {
  if (compareAtPrice != null && compareAtPrice > price) {
    return parseFloat((compareAtPrice - price).toFixed(2));
  }
  return 0;
}

// Build a GA4 item object from a [data-line-item] element
function formatItemFromLineElement(el, index) {
  const ardeneStyle = el.getAttribute('data-product-style') || '';
  const sku = el.getAttribute('data-product-sku') || '';
  const title = el.getAttribute('data-title') || '';
  const qtyRaw = el.getAttribute('data-quantity') || '0';
  const priceRaw = el.getAttribute('data-price') || '0';
  const internalCategoryCsv = el.getAttribute('data-internal-category') || '';

  const quantity = parseInt(qtyRaw, 10) || 1;
  const price = parseCentsToMajorUnits(priceRaw);

  // Read compare price from nested price-component (price.liquid stores total line compare price)
  const comparePriceEl = el.querySelector('[data-price-compare]');
  const compareCents = parseInt(comparePriceEl?.getAttribute('data-price-cents') || '0', 10);
  // Divide by quantity to get per-unit compare price
  const compareAtPrice = compareCents > 0 ? parseCentsToMajorUnits(compareCents) / quantity : undefined;
  const discount = calculateDiscount(price, compareAtPrice);

  const item = {
    item_id: ardeneStyle,
    item_sku: sku,
    item_name: title,
    item_brand: 'Ardene',
    quantity,
    price,
    compareAtPrice,
    discount,
    index: typeof index === 'number' ? index + 1 : undefined,
    internalCategory: internalCategoryCsv,
  };

  return applyCategoryFieldsToItem(item, internalCategoryCsv);
}

function collectLineItems(container = document, selector = '[data-line-item]') {
  const lineItemEls = Array.from(container.querySelectorAll(selector));
  return lineItemEls.map((el, idx) => formatItemFromLineElement(el, idx));
}

// Build a GA4 item object from a [data-product-card] element
function formatItemFromProductCard(card) {
  const productForm = card.querySelector('product-form');
  const titleEl = card.querySelector('[data-title]');
  const title = (titleEl?.textContent || '').trim();
  const sku = productForm?.getAttribute('data-product-sku') || '';
  const ardeneStyle = productForm?.getAttribute('data-product-style') || '';
  const internalCategoryCsv = productForm?.getAttribute('data-internal-category') || '';
  // Prefer available variant price from embedded product JSON
  // Shopify prices in JSON are in cents (e.g., 8000 = $80.00)
  let priceAmount;
  let compareAtPriceAmount;
  try {
    const productJson = productForm?.getAttribute('data-product');
    const finalSale = productForm.querySelector('[data-price-sitewide][data-active="true"]')?.getAttribute('data-price-cents') || null;
    const product = productJson ? JSON.parse(productJson) : null;
    const variant =
      product?.variants?.find((v) => v?.available) || product?.variants?.[0] || null;
    if (finalSale) {
      priceAmount = parseCentsToMajorUnits(finalSale);
    }
    else if (variant?.price != null) {
      priceAmount = parseCentsToMajorUnits(variant.price);
    }
    else if (priceAmount == null && product?.price != null) {
      priceAmount = parseCentsToMajorUnits(product.price);
    }
    // Extract compare_at_price from the variant
    if (variant?.compare_at_price != null) {
      compareAtPriceAmount = parseCentsToMajorUnits(variant.compare_at_price);
    }
  } catch {
    // Ignore JSON parse errors and continue with defaults
  }
  const discount = calculateDiscount(priceAmount, compareAtPriceAmount);
  return {
    item_id: ardeneStyle,
    item_sku: sku,
    item_name: title,
    item_brand: 'Ardene',
    price: priceAmount,
    compareAtPrice: compareAtPriceAmount,
    discount,
    quantity: 1,
    internalCategory: internalCategoryCsv
  };
}

function collectProductCards(container = document, selector = '[data-product-card]') {
  const cards = Array.from(container.querySelectorAll(selector));
  return cards.map((card) => {
    try {
      return formatItemFromProductCard(card);
    } catch {
      return null;
    }
  }).filter(Boolean);
}

/**
 * Dispatch analytics event when product is selected
 * @private
 * @param {CustomEvent} e - Event containing product details
 */
function handleSelectItem(e) {
  const { product } = e.detail;
  if (!product) return;

  try {
    if (!window.Shopify?.analytics?.publish) {
      console.error('Shopify.analytics.publish not available');
      return;
    }

    const variant =
      product.selected_variant ||
      product.variants?.find((v) => v?.available) ||
      product.variants?.[0] ||
      {};

    const price = parseCentsToMajorUnits(variant.price ?? product.price);
    const compareAtPrice =
      variant.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : null;

    const discount = calculateDiscount(price, compareAtPrice);
    const item = {
      item_id: product.ardeneStyle?.toString() || '',
      item_sku: variant.sku || '',
      item_name: product.title || '',
      item_brand: 'Ardene',
      price,
      quantity: 1,
      discount,
      internalCategory: product.internalCategory || '',
    };

    if (compareAtPrice != null) {
      item.compareAtPrice = compareAtPrice;
    }

    const currency = window.Shopify?.currency?.active || 'CAD';
    window.Shopify.analytics.publish('ardene:select_item', {
      item,
      listId: window.collection?.handle,
      listName: window.collection?.title,
      currency,
    });
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}
function handleProductChange(e) {
  const { variant, product } = e.detail;

  try {
    // Publish through Shopify's analytics API
    if (window.Shopify?.analytics?.publish) {
      const currencyCode = window.Shopify?.currency?.active || 'CAD';
      let priceAmount;
      if (variant?.price != null) {
        priceAmount = parseCentsToMajorUnits(variant.price);
      } else if (product?.price != null) {
        priceAmount = parseCentsToMajorUnits(product.price);
      } else {
        priceAmount = 0;
      }

      const compareAtPriceAmount = variant?.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : undefined;
      const discount = calculateDiscount(priceAmount, compareAtPriceAmount);

      const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
      const imageSrc =
        variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        product?.featured_image ||
        (Array.isArray(product?.images) && product.images.length > 0 ? product.images[0]?.src : '') ||
        '';

      const payload = {
        productVariant: {
          price: {
            amount: priceAmount || 0,
            currencyCode,
          },
          compareAtPrice: compareAtPriceAmount != null
            ? { amount: compareAtPriceAmount, currencyCode }
            : undefined,
          discount,
          product: {
            title: product?.title || '',
            vendor: product?.vendor || 'Ardene',
            id: (product?.id != null ? String(product.id) : undefined),
            untranslatedTitle: product?.title || '',
            url: product?.handle ? `${rootUrl}products/${product.handle}` : undefined,
            productType: product?.type || '',
            internalCategory: product?.internalCategory || '',
            ardeneStyle: product?.ardeneStyle || '',
            complexId: product?.complexId || ''
          },
          id: (variant?.id != null ? String(variant.id) : undefined),
          image: {
            src: imageSrc,
          },
          sku: variant?.sku || product?.productSku || '',
          title: variant?.title || '',
          untranslatedTitle: variant?.title || '',
        },
      };

      window.Shopify.analytics.publish('ardene:product_viewed', payload);
    } else {
      console.error('Shopify.analytics.publish not available');
    }
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}
function handleCartAdd(e) {
  const { variant, product } = e.detail;

  try {
    // Publish through Shopify's analytics API
    if (window.Shopify?.analytics?.publish) {
      const currencyCode = window.Shopify?.currency?.active || 'CAD';
      let priceAmount;
      if (variant?.price != null) {
        priceAmount = parseCentsToMajorUnits(variant.price);
      } else if (product?.price != null) {
        priceAmount = parseCentsToMajorUnits(product.price);
      } else {
        priceAmount = undefined;
      }

      const compareAtPriceAmount = variant?.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : undefined;
      const discount = calculateDiscount(priceAmount, compareAtPriceAmount);

      const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
      const imageSrc =
        variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        product?.featured_image ||
        (Array.isArray(product?.images) && product.images.length > 0 ? product.images[0]?.src : '') ||
        '';

      const payload = {
        productVariant: {
          price: {
            amount: priceAmount,
            currencyCode,
          },
          compareAtPrice: compareAtPriceAmount != null
            ? { amount: compareAtPriceAmount, currencyCode }
            : undefined,
          discount,
          product: {
            title: product?.title || '',
            vendor: product?.vendor || 'Ardene',
            id: (product?.id != null ? String(product.id) : undefined),
            untranslatedTitle: product?.title || '',
            url: product?.handle ? `${rootUrl}products/${product.handle}` : undefined,
            productType: product?.type || '',
            internalCategory: product?.internalCategory || '',
            ardeneStyle: product?.ardeneStyle || '',
            complexId: product?.complexId || ''
          },
          id: (variant?.id != null ? String(variant.id) : undefined),
          image: {
            src: imageSrc,
          },
          sku: variant?.sku || product?.productSku || '',
          title: variant?.title || '',
          untranslatedTitle: variant?.title || '',
        },
      };

      window.Shopify.analytics.publish('ardene:product_added_to_cart', payload);
    } else {
      console.error('Shopify.analytics.publish not available');
    }
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}
function handleAddToWishlist(e) {
  const { variant, product } = e.detail;

  try {
    // Publish through Shopify's analytics API
    if (window.Shopify?.analytics?.publish) {
      const currencyCode = window.Shopify?.currency?.active || 'CAD';
      let priceAmount;
      if (variant?.price != null) {
        priceAmount = parseCentsToMajorUnits(variant.price);
      } else if (product?.price != null) {
        priceAmount = parseCentsToMajorUnits(product.price);
      } else {
        priceAmount = undefined;
      }

      const compareAtPriceAmount = variant?.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : undefined;
      const discount = calculateDiscount(priceAmount, compareAtPriceAmount);

      const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
      const imageSrc =
        variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        product?.featured_image ||
        (Array.isArray(product?.images) && product.images.length > 0 ? product.images[0]?.src : '') ||
        '';

      const payload = {
        productVariant: {
          price: {
            amount: priceAmount,
            currencyCode,
          },
          compareAtPrice: compareAtPriceAmount != null
            ? { amount: compareAtPriceAmount, currencyCode }
            : undefined,
          discount,
          product: {
            title: product?.title || '',
            vendor: product?.vendor || 'Ardene',
            id: (product?.id != null ? String(product.id) : undefined),
            untranslatedTitle: product?.title || '',
            url: product?.handle ? `${rootUrl}products/${product.handle}` : undefined,
            productType: product?.type || '',
            internalCategory: product?.internalCategory || '',
            ardeneStyle: product?.ardeneStyle || '',
            complexId: product?.complexId || ''
          },
          id: (variant?.id != null ? String(variant.id) : undefined),
          image: {
            src: imageSrc,
          },
          sku: variant?.sku || product?.productSku || '',
          title: variant?.title || '',
          untranslatedTitle: variant?.title || '',
        },
      };

      window.Shopify.analytics.publish('ardene:add_to_wishlist', payload);
    } else {
      console.error('Shopify.analytics.publish not available');
    }
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}

function handleProductRemovedFromCart(e) {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;

    const item = e.detail?.item;
    if (!item) return;

    const currency = getActiveCurrencyOrDefault();
    const quantity = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const total = (quantity * price).toFixed(2);

    // Shape a cartLine-like object for the custom pixel subscriber
    const cartLine = {
      merchandise: {
        id: item.item_sku || item.key || '',
        sku: item.item_sku || '',
        product: {
          id: item.item_id != null ? String(item.item_id) : '',
          title: item.item_name || '',
          productType: item.internalCategory || '',
          ardeneStyle: item.item_id || '',
          internalCategory: item.internalCategory || '',
        },
      },
      cost: {
        totalAmount: {
          amount: total,
          currencyCode: currency,
        },
      },
      compareAtPrice: item.compareAtPrice != null
        ? { amount: String(item.compareAtPrice), currencyCode: currency }
        : undefined,
      discount: item.discount || 0,
      quantity,
    };

    publishAnalytics('ardene:product_removed_from_cart', {
      cartLine,
    });
  } catch (error) {
    console.error('Error dispatching product_removed_from_cart analytics event:', error);
  }
}

function handleCartViewed() {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;

    const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
    const cartUrl = window.Shopify?.routes?.cart_url || window.routes?.cart_url || `${rootUrl}cart`;

    fetch(`${cartUrl}.js`, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`Failed to fetch cart: ${res.status}`))))
      .then((cart) => {
        const currency = cart?.currency || getActiveCurrencyOrDefault();
        const value = typeof cart?.total_price === 'number' ? cart.total_price / 100 : undefined;

        const items = collectLineItems();

        publishAnalytics('ardene:view_cart', {
          cartData: { items },
          currency,
          value,
        });
      })
      .catch((err) => {
        console.error('Error fetching cart for view_cart:', err);
      });
  } catch (error) {
    console.error('Error dispatching view_cart analytics event:', error);
  }
}

function handleProductGridViewed(e) {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;

    // Support both event objects (e.detail?.grid) and direct DOM element calls
    const grid = e?.detail?.grid || (e?.querySelectorAll ? e : document);
    const listId = window.collection?.id || '';
    const listName = window.collection?.title || '';

    const products = collectProductCards(grid);
    if (!products.length) return;

    const currency = window.Shopify?.currency?.active || 'CAD';
    window.Shopify.analytics.publish('ardene:view_item_list', {
      listId,
      listName,
      products,
      currency,
    });
  } catch (error) {
    console.error('Error dispatching view_item_list analytics event:', error);
  }
}

function handleBeginCheckoutClick(e) {
  const trigger = e.target.closest('[data-cart-submit]');
  if (!trigger) return;

  // Only handle the minicart checkout link (anchor with href to /checkout)
  const isInMinicart = Boolean(trigger.closest('#minicart, minicart-component'));
  const isLink = trigger.tagName === 'A';
  const href = isLink ? trigger.getAttribute('href') : null;
  if (!isInMinicart || !isLink || !href || !href.includes('/checkout')) return;

  // Prevent default navigation while we build/send analytics
  e.preventDefault();
  if (trigger.getAttribute('data-processing') === 'true') return;
  trigger.setAttribute('data-processing', 'true');
  trigger.setAttribute('aria-disabled', 'true');

  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) {
      window.location.assign(href);
      return;
    }

    // Build items array from DOM [data-line-item] entries in the minicart
    const items = collectLineItems();

    const currency = getActiveCurrencyOrDefault();
    const value = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

    publishAnalytics('ardene:begin_checkout', {
      cartData: { items },
      currency,
      value,
    });
    // Allow a short delay to ensure any subscribers process synchronously
    setTimeout(() => {
      window.location.assign(href);
    }, 100);
  } catch (error) {
    console.error('Error dispatching begin_checkout analytics event:', error);
    // Fail-safe: still proceed to checkout
    window.location.assign(href);
  }
}

/**
 * Initialize analytics tracking
 */
function initAnalytics() {
  document.addEventListener('select-item', handleSelectItem);
  document.addEventListener('product-change', handleProductChange);
  document.addEventListener('product-viewed', handleProductChange);
  document.addEventListener('product-added-to-cart', handleCartAdd);
  document.addEventListener('add-to-wishlist', handleAddToWishlist);
  document.addEventListener('cart-viewed', handleCartViewed);
  document.addEventListener('begin-checkout', handleBeginCheckoutClick);
  document.addEventListener('product-grid-ready', handleProductGridViewed);
  document.addEventListener('product-removed-from-cart', handleProductRemovedFromCart);
}
/** Publishes hashed email and locale for the custom pixel (each full page load). */
async function publishCustomerContextForPixel() {
  try {
    if (!window.Shopify?.analytics?.publish) return;
    const customerEmailSha256 = await getCustomerEmailSha256ForPixel();
    const localeData = getLocaleData();
    publishAnalytics(
      'ardene:customer_context',
      customerEmailSha256 ? { email: customerEmailSha256, ...localeData } : localeData
    );
  } catch (error) {
    console.error('Error publishing ardene:customer_context analytics event:', error);
  }
}

async function maybePublishCustomerLoggedIn() {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;
    if (!window.sessionStorage) return;

    const alreadyFired = window.sessionStorage.getItem('customerLoginEventFired') === 'true';
    if (!window.customer) {
      window.sessionStorage.setItem('customerLoginEventFired', 'false');
      return;
    }
    if (alreadyFired) return;
    const customerEmailSha256 = await getCustomerEmailSha256ForPixel();
    if (!customerEmailSha256) return;
    console.log('Publishing ardene:customer_logged_in analytics event');
    const localeData = getLocaleData();
    publishAnalytics('ardene:customer_logged_in', { email: customerEmailSha256, ...localeData });
    window.sessionStorage.setItem('customerLoginEventFired', 'true');
  } catch (error) {
    console.error('Error dispatching ardene:customer_logged_in analytics event:', error);
  }
}
const productViewed = (productFormEl) => {
  const product = productFormEl.parseJsonAttribute('data-product');
  const ardeneStyle = productFormEl.getAttribute('data-product-style');
  const sku = productFormEl.getAttribute('data-product-sku');
  const internalCategory = productFormEl.getAttribute('data-internal-category');
  const complexId = productFormEl.getAttribute('data-complex-id');
  // Extend product object with additional attributes for unified access
  if (product) {
    const extendedProduct = {
      ...product,
      ardeneStyle: ardeneStyle,
      internalCategory: internalCategory,
      complexId: complexId,
      productSku: sku,
    };
    handleProductChange({ detail: { product: extendedProduct, variant: null } });
  }
  return null;
}

initPandectesConsentTracking();

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  void (async () => {
    await publishCustomerContextForPixel();
    initAnalytics();
    const productFormEl = document.querySelector('.js-product-page product-form');
    if (productFormEl) {
      productViewed(productFormEl);
    }
    const productGridEl = document.querySelector('product-grid');
    if (productGridEl) {
      handleProductGridViewed(productGridEl);
    }
    await maybePublishCustomerLoggedIn();
  })();
});
